import React from "react";
import { Task } from "../../types";
import { stripHtmlAndImages } from "../../lib/utils";
import HighlightedText from "../ui/HighlightedText";

interface SearchResultCardProps {
  task: Task;
  searchTerm: string;
  onClick: () => void;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({
  task,
  searchTerm,
  onClick,
}) => {
  const getDescriptionExcerpt = (): string => {
    const plainText = stripHtmlAndImages(task.description);
    if (!plainText) return "";

    const lowerText = plainText.toLowerCase();
    const lowerTerm = searchTerm.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerTerm);

    if (matchIndex !== -1) {
      // Show context around the match
      const start = Math.max(0, matchIndex - 50);
      const end = Math.min(plainText.length, matchIndex + searchTerm.length + 50);
      let excerpt = plainText.slice(start, end);

      if (start > 0) excerpt = "..." + excerpt;
      if (end < plainText.length) excerpt = excerpt + "...";

      return excerpt;
    } else {
      // No match in description, show first ~100 chars
      return plainText.slice(0, 100) + (plainText.length > 100 ? "..." : "");
    }
  };

  const descriptionExcerpt = getDescriptionExcerpt();

  // Get all matching subtasks (up to 2)
  const matchingSubtasks = task.subtasks
    ?.filter((st) => st.title.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 2) || [];

  return (
    <div
      onClick={onClick}
      className="p-4 border border-warm-border rounded-lg hover:bg-warm-bg-elevated cursor-pointer transition-colors"
    >
      {/* List (hidden for listless tasks) */}
      {task.list && (
        <div className="flex items-center gap-1.5 mb-2 text-xs text-warm-text-tertiary">
          <span>{task.listEmoji}</span>
          <span>{task.list}</span>
        </div>
      )}

      {/* Title */}
      <div dir="auto" className="font-semibold text-warm-text-primary mb-1">
        <HighlightedText text={task.title} searchTerm={searchTerm} />
      </div>

      {/* Description */}
      {descriptionExcerpt && (
        <div className="text-sm text-warm-text-secondary mb-2">
          <HighlightedText text={descriptionExcerpt} searchTerm={searchTerm} />
        </div>
      )}

      {/* Matching Subtasks */}
      {matchingSubtasks.length > 0 && (
        <div className="mt-2 space-y-1">
          {matchingSubtasks.map((subtask) => (
            <div
              key={subtask.id}
              className="text-sm text-warm-text-secondary flex items-start gap-1"
            >
              <span className="mt-0.5">↳</span>
              <span>
                "<HighlightedText text={subtask.title} searchTerm={searchTerm} />"
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResultCard;
